<template>
  <div class="capture-flow min-h-screen flex flex-col text-white">
    <!-- ============ Camera overlay (sequence, retake or damage) ============ -->
    <CameraCapture
      v-if="activeCategory"
      ref="camera"
      :label="activeCategory.label"
      :hint="activeCategory.hint"
      :progress-label="cameraProgressLabel"
      :progress-percent="cameraProgressPercent"
      @close="onCameraClose"
      @captured="onCaptured"
    />

    <!-- ============ Detecting camera ============ -->
    <div v-else-if="phase === 'detecting'" class="flex-grow flex items-center justify-center">
      <Icon type="circle-notch" class="text-3xl" spinner />
    </div>

    <!-- ============ Confirmation ============ -->
    <div v-else-if="phase === 'done'" class="flex-grow flex flex-col items-center justify-center p-8 text-center">
      <div class="done-check mb-6">
        <Icon type="check" fa-style="fas" class="text-4xl" />
      </div>
      <div class="text-2xl font-semibold mb-2">All done!</div>
      <p class="opacity-70 mb-8 max-w-[300px]">
        Your photos have been sent to the dealership. They'll start valuing your vehicle right away.
      </p>
      <Button label="Back to your valuation" size="lg" class="accent-button" @click="leaveToHub" />
    </div>

    <!-- ============ Uploading ============ -->
    <div v-else-if="phase === 'uploading'" class="flex-grow flex flex-col items-center justify-center p-8 text-center">
      <Icon type="circle-notch" class="text-4xl mb-6" spinner />
      <div class="text-xl font-semibold mb-2">Submitting your photos</div>
      <p class="text-sm opacity-70 mb-6">Uploading photo {{ uploadingIndex + 1 }} of {{ uploadQueueLength }}</p>
      <div class="progress-track w-full max-w-[280px]">
        <div class="progress-fill" :style="{ width: overallUploadPercent + '%' }" />
      </div>
    </div>

    <!-- ============ Review list ============ -->
    <div v-else-if="phase === 'review'" class="w-full max-w-[560px] mx-auto flex flex-col flex-grow px-5 pt-4 pb-6">
      <!-- Top bar -->
      <div class="flex items-center justify-between mb-6">
        <button class="close-button" aria-label="Back" @click="confirmLeaveToHub">
          <Icon type="arrow-left" fa-style="far" class="text-lg" />
        </button>
        <div class="text-sm font-medium opacity-80">Review</div>
        <div class="w-[36px]" />
      </div>

      <div class="text-center mb-6">
        <div class="text-2xl font-semibold">{{ hasCamera ? 'Check your photos' : 'Add your photos' }}</div>
        <p class="text-sm opacity-70 mt-2 max-w-[340px] mx-auto">
          <template v-if="hasCamera">Happy with everything? You can retake any photo before submitting.</template>
          <template v-else>Click each item to upload a clear, well-lit photo of your vehicle.</template>
        </p>
      </div>

      <!-- Category list -->
      <div class="review-list mb-8">
        <div
          v-for="category in photoCategories"
          :key="category.id"
          class="review-row"
          :class="{ 'review-row-clickable': !hasCamera }"
          @click="!hasCamera && promptUpload(category)"
        >
          <div class="review-thumb">
            <img v-if="photos[category.id]" :src="photos[category.id].url" :alt="category.label" class="w-full h-full object-cover" />
            <div v-else class="w-full h-full flex items-center justify-center opacity-40">
              <Icon :type="category.icon" fa-style="far" class="text-sm" />
            </div>
          </div>
          <div class="flex-grow min-w-0">
            <div class="font-medium truncate">{{ category.label }}</div>
            <div class="text-xs opacity-60 truncate">{{ category.description }}</div>
          </div>
          <Icon v-if="photos[category.id]" type="circle-check" fa-style="fas" class="text-lg text-success flex-shrink-0" />
          <Icon v-else type="circle" fa-style="far" class="text-lg opacity-30 flex-shrink-0" />
          <button v-if="hasCamera" class="retake-link flex-shrink-0" @click="startRetake(category)">Retake</button>
          <button v-else class="retake-link flex-shrink-0" @click.stop="promptUpload(category)">
            {{ photos[category.id] ? 'Replace' : 'Upload' }}
          </button>
        </div>
      </div>

      <!-- Damage section -->
      <div class="flex-grow">
        <div class="text-lg font-semibold">Damage close ups</div>
        <p class="text-sm opacity-70 mt-1 mb-4">
          Add close-up photos of any scratches, dents or scuffs. Being upfront about damage gets you the most accurate offer.
        </p>

        <div v-if="damagePhotos.length" class="grid grid-cols-3 gap-3 mb-4">
          <div v-for="(photo, index) in damagePhotos" :key="photo.url" class="damage-thumb">
            <img :src="photo.url" :alt="`Damage photo ${index + 1}`" class="w-full h-full object-cover" />
            <button class="damage-remove" :aria-label="`Remove damage photo ${index + 1}`" @click="removeDamagePhoto(index)">
              <Icon type="xmark" fa-style="far" class="text-xs" />
            </button>
          </div>
        </div>

        <Button
          :label="damagePhotos.length ? 'Add more damage' : 'Add damage photo'"
          :icon="hasCamera ? 'camera' : 'upload'"
          block
          outlined
          severity="secondary"
          class="outline-button"
          @click="addDamagePhoto"
        />
      </div>

      <!-- Submit -->
      <div class="mt-8">
        <div v-if="uploadError" class="upload-error mb-4">
          <Icon type="triangle-exclamation" fa-style="far" class="mr-2" />
          {{ uploadError }}
        </div>
        <div v-if="!allPhotosPresent" class="text-sm opacity-60 text-center mb-3">
          Add {{ missingCount }} more {{ missingCount === 1 ? 'photo' : 'photos' }} to continue
        </div>
        <Button
          :label="uploadError ? 'Retry upload' : 'Submit photos'"
          icon="arrow-right"
          icon-pos="right"
          block
          size="lg"
          class="accent-button"
          :disabled="!allPhotosPresent"
          @click="onSubmitClicked"
        />
      </div>
    </div>

    <!-- Hidden file input (devices without a camera) -->
    <input ref="uploadInput" type="file" accept="image/*" :multiple="uploadTarget === 'damage'" class="hidden" @change="onFilesSelected" />
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import Swal from 'sweetalert2'

import Button from '@/components/button/Button.vue'
import Icon from '@/components/icon/Icon.vue'
import CameraCapture from '@/components/CameraCapture.vue'
import { PHOTO_CATEGORIES, DAMAGE_CATEGORY, valuationUploadEndpoint } from '@/composables/useValuation'

const route = useRoute()
const router = useRouter()

const valuationId = route.params.vid
const uploadUrl = valuationUploadEndpoint(valuationId)
const photoCategories = PHOTO_CATEGORIES

const camera = ref(null)
const uploadInput = ref(null)

// True when the device has a camera we can use; false → file-upload mode
const hasCamera = ref(true)
// Which item the hidden file input is uploading for: a category object or 'damage'
const uploadTarget = ref(null)

// detecting | capture | review | uploading | done
const phase = ref('detecting')
// sequence | retake | damage | null — which camera session is open
const cameraMode = ref(null)

const sequenceIndex = ref(0)
const retakeCategory = ref(null)

// categoryId -> { blob, url, uploaded }
const photos = ref({})
// [{ blob, url, uploaded }]
const damagePhotos = ref([])

const uploadingIndex = ref(0)
const uploadQueueLength = ref(0)
const currentFilePercent = ref(0)
const uploadError = ref(null)

// ---------- Camera session helpers ----------

const activeCategory = computed(() => {
  if (cameraMode.value === 'sequence') return PHOTO_CATEGORIES[sequenceIndex.value]
  if (cameraMode.value === 'retake') return retakeCategory.value
  if (cameraMode.value === 'damage') return DAMAGE_CATEGORY
  return null
})

const cameraProgressLabel = computed(() => {
  if (cameraMode.value === 'sequence') return `${sequenceIndex.value + 1} of ${PHOTO_CATEGORIES.length}`
  if (cameraMode.value === 'retake') return 'Retake'
  if (cameraMode.value === 'damage') return `Damage photo ${damagePhotos.value.length + 1}`
  return ''
})

const cameraProgressPercent = computed(() => {
  if (cameraMode.value !== 'sequence') return null
  return Math.round((Object.keys(photos.value).length / PHOTO_CATEGORIES.length) * 100)
})

function setPhoto(categoryId, blob) {
  const existing = photos.value[categoryId]
  if (existing) {
    URL.revokeObjectURL(existing.url)
  }
  photos.value[categoryId] = { blob, url: URL.createObjectURL(blob), uploaded: false }
}

function onCaptured(blob) {
  if (cameraMode.value === 'sequence') {
    setPhoto(PHOTO_CATEGORIES[sequenceIndex.value].id, blob)
    if (sequenceIndex.value < PHOTO_CATEGORIES.length - 1) {
      sequenceIndex.value++
      camera.value.reset()
    } else {
      cameraMode.value = null
      phase.value = 'review'
    }
  } else if (cameraMode.value === 'retake') {
    setPhoto(retakeCategory.value.id, blob)
    retakeCategory.value = null
    cameraMode.value = null
  } else if (cameraMode.value === 'damage') {
    damagePhotos.value.push({ blob, url: URL.createObjectURL(blob), uploaded: false })
    cameraMode.value = null
  }
}

function onCameraClose() {
  if (cameraMode.value === 'sequence') {
    confirmLeaveToHub()
  } else {
    retakeCategory.value = null
    cameraMode.value = null
  }
}

function startRetake(category) {
  retakeCategory.value = category
  cameraMode.value = 'retake'
}

function startDamageCapture() {
  cameraMode.value = 'damage'
}

function addDamagePhoto() {
  if (hasCamera.value) {
    startDamageCapture()
  } else {
    promptUpload('damage')
  }
}

// ---------- File-upload mode (devices without a camera) ----------

async function detectCamera() {
  if (!navigator.mediaDevices?.getUserMedia) return false
  if (!navigator.mediaDevices.enumerateDevices) return true

  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices.some(device => device.kind === 'videoinput')
  } catch {
    // If detection fails, let the camera flow try; it has its own fallback
    return true
  }
}

function promptUpload(target) {
  uploadTarget.value = target
  // Let the `multiple` attribute update before opening the picker
  nextTick(() => uploadInput.value.click())
}

function onFilesSelected(event) {
  const files = [...event.target.files].filter(file => file.type.startsWith('image/'))
  event.target.value = ''
  if (!files.length) return

  if (uploadTarget.value === 'damage') {
    files.forEach(file => {
      damagePhotos.value.push({ blob: file, url: URL.createObjectURL(file), uploaded: false })
    })
  } else if (uploadTarget.value) {
    setPhoto(uploadTarget.value.id, files[0])
  }
  uploadTarget.value = null
}

const allPhotosPresent = computed(() => PHOTO_CATEGORIES.every(category => photos.value[category.id]))
const missingCount = computed(() => PHOTO_CATEGORIES.filter(category => !photos.value[category.id]).length)

function removeDamagePhoto(index) {
  URL.revokeObjectURL(damagePhotos.value[index].url)
  damagePhotos.value.splice(index, 1)
}

// ---------- Leaving ----------

function leaveToHub() {
  router.push(`/valuation/${valuationId}`)
}

function confirmLeaveToHub() {
  if (!Object.keys(photos.value).length) {
    leaveToHub()
    return
  }

  Swal.fire({
    title: 'Leave without submitting?',
    text: "Your photos haven't been submitted yet and will be lost.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Stay',
    cancelButtonText: 'Leave',
    confirmButtonColor: 'var(--manufacturer-color)'
  }).then(result => {
    if (result.dismiss === Swal.DismissReason.cancel) {
      leaveToHub()
    }
  })
}

// ---------- Submit ----------

function onSubmitClicked() {
  if (!damagePhotos.value.length && !uploadError.value) {
    Swal.fire({
      title: 'No damage photos',
      text: 'Close ups of any scratches, dents or scuffs help the dealership give you the most accurate offer. Would you like to add some?',
      icon: 'question',
      showDenyButton: true,
      confirmButtonText: 'Add damage photos',
      denyButtonText: 'Submit without',
      confirmButtonColor: 'var(--manufacturer-color)'
    }).then(result => {
      if (result.isConfirmed) {
        addDamagePhoto()
      } else if (result.isDenied) {
        submitAllPhotos()
      }
    })
  } else {
    submitAllPhotos()
  }
}

function buildUploadQueue() {
  const queue = PHOTO_CATEGORIES.map(category => ({
    fileName: `${category.id}.jpg`,
    item: photos.value[category.id]
  }))
  damagePhotos.value.forEach((item, index) => {
    queue.push({ fileName: `damage-${index + 1}.jpg`, item })
  })
  return queue.filter(entry => entry.item)
}

async function submitAllPhotos() {
  const queue = buildUploadQueue()
  const pending = queue.filter(entry => !entry.item.uploaded)
  if (!pending.length) {
    phase.value = 'done'
    return
  }

  phase.value = 'uploading'
  uploadError.value = null
  uploadQueueLength.value = pending.length

  for (let i = 0; i < pending.length; i++) {
    uploadingIndex.value = i
    currentFilePercent.value = 0

    const entry = pending[i]
    const formData = new FormData()
    formData.append('file', entry.item.blob, entry.fileName)
    formData.append('uploadedBy', null)
    formData.append('uploadedByType', 'customer')

    try {
      // No cookies — auth is via the api_key query param; a stray session cookie causes a 401
      await axios.post(uploadUrl, formData, {
        withCredentials: false,
        onUploadProgress: progressEvent => {
          currentFilePercent.value = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        }
      })
      entry.item.uploaded = true
    } catch (error) {
      console.error(`Upload of ${entry.fileName} failed: ${error.message}`)
      uploadError.value = 'Some photos failed to upload. Please check your connection and try again.'
      phase.value = 'review'
      return
    }
  }

  phase.value = 'done'
}

const overallUploadPercent = computed(() => {
  if (!uploadQueueLength.value) return 0
  return Math.round(((uploadingIndex.value + currentFilePercent.value / 100) / uploadQueueLength.value) * 100)
})

// ---------- Lifecycle ----------

onMounted(async () => {
  hasCamera.value = await detectCamera()

  if (hasCamera.value) {
    phase.value = 'capture'
    cameraMode.value = 'sequence'
    sequenceIndex.value = 0
  } else {
    // No camera (e.g. desktop) — skip the guided flow and let the customer upload files
    cameraMode.value = null
    phase.value = 'review'
  }
})

onBeforeUnmount(() => {
  Object.values(photos.value).forEach(photo => URL.revokeObjectURL(photo.url))
  damagePhotos.value.forEach(photo => URL.revokeObjectURL(photo.url))
})
</script>

<style scoped>
.capture-flow {
  background-color: #0b1526;
}

.close-button {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.12);
}

.review-list {
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  overflow: hidden;
}
.review-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
}
.review-row + .review-row {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.review-row-clickable {
  cursor: pointer;
}
.review-row-clickable:hover {
  background-color: rgba(255, 255, 255, 0.05);
}
.review-thumb {
  width: 56px;
  height: 42px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background-color: rgba(255, 255, 255, 0.06);
}

.retake-link {
  font-size: 0.875rem;
  font-weight: 500;
  color: color-mix(in srgb, var(--manufacturer-color) 60%, white 40%);
  padding: 6px 8px;
}
.retake-link:hover {
  text-decoration: underline;
}

.damage-thumb {
  position: relative;
  aspect-ratio: 4 / 3;
  border-radius: 10px;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.06);
}
.damage-remove {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(11, 21, 38, 0.8);
  color: white;
}

.progress-track {
  height: 6px;
  border-radius: 100px;
  background-color: rgba(255, 255, 255, 0.15);
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  border-radius: 100px;
  background-color: var(--manufacturer-color);
  transition: width 0.3s ease;
}

.done-check {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--bg-success-solid, #16a34a);
  color: white;
}

.upload-error {
  background-color: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.4);
  color: #fca5a5;
  border-radius: 12px;
  padding: 10px 14px;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
}

.accent-button {
  background-color: var(--manufacturer-color) !important;
}

.outline-button {
  color: white !important;
  border-color: rgba(255, 255, 255, 0.4) !important;
}
.outline-button:enabled:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
}
</style>
